import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Save, Trash2, CheckCircle2, XCircle, Ban } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const STATUS_META = {
  available: { label: 'Available', icon: CheckCircle2, color: 'text-emerald-300', dot: 'bg-emerald-400' },
  booked: { label: 'Booked', icon: XCircle, color: 'text-rose-300', dot: 'bg-rose-400' },
  unavailable: { label: 'Unavailable', icon: Ban, color: 'text-slate-400', dot: 'bg-slate-500' },
};

const fmtDate = (d) => format(d, 'yyyy-MM-dd');

/**
 * CalendarManager — artist-side editor: select dates and toggle status.
 * Hits POST /api/artists/me/calendar (bulk upsert).
 */
export const CalendarManager = ({ artistId }) => {
  const [entries, setEntries] = useState({}); // {YYYY-MM-DD: status}
  const [dirty, setDirty] = useState({});
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!artistId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/artists/${artistId}/calendar`);
      const map = {};
      data.entries.forEach((e) => { map[e.date] = e.status; });
      setEntries(map);
      setDirty({});
    } finally { setLoading(false); }
  }, [artistId]);
  useEffect(() => { load(); }, [load]);

  const merged = useMemo(() => ({ ...entries, ...dirty }), [entries, dirty]);

  const setStatusForSelected = (status) => {
    if (selected.length === 0) {
      toast.info('Pick one or more dates first');
      return;
    }
    const next = { ...dirty };
    selected.forEach((d) => { next[fmtDate(d)] = status; });
    setDirty(next);
  };

  const removeSelected = () => {
    if (selected.length === 0) return;
    const next = { ...dirty };
    selected.forEach((d) => { next[fmtDate(d)] = '__remove__'; });
    setDirty(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Apply removals
      const toDelete = Object.entries(dirty).filter(([, v]) => v === '__remove__').map(([k]) => k);
      const toUpsert = Object.entries(dirty).filter(([, v]) => v !== '__remove__').map(([date, status]) => ({ date, status }));
      for (const d of toDelete) {
        try { await api.delete(`/artists/me/calendar/${d}`); } catch (_e) { /* ignore */ }
      }
      if (toUpsert.length) {
        await api.post('/artists/me/calendar', { entries: toUpsert });
      }
      toast.success('Calendar saved');
      await load();
      setSelected([]);
    } catch (err) {
      toast.error('Could not save calendar', { description: err?.response?.data?.detail });
    } finally { setSaving(false); }
  };

  const modifiers = useMemo(() => {
    const av = [], bo = [], un = [];
    Object.entries(merged).forEach(([date, status]) => {
      if (status === '__remove__') return;
      const d = parseISO(date);
      if (status === 'available') av.push(d);
      else if (status === 'booked') bo.push(d);
      else if (status === 'unavailable') un.push(d);
    });
    return { available: av, booked: bo, unavailable: un };
  }, [merged]);

  const modifiersClassNames = {
    available: 'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 ring-1 ring-emerald-400/30',
    booked: 'bg-rose-500/20 text-rose-200 hover:bg-rose-500/30 ring-1 ring-rose-400/30',
    unavailable: 'bg-slate-500/20 text-slate-300 hover:bg-slate-500/30 ring-1 ring-slate-400/30 line-through',
  };

  if (loading) return <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-fuchsia-400" /></div>;

  const dirtyCount = Object.keys(dirty).length;

  return (
    <div data-testid="artist-calendar-manager" className="space-y-4">
      <div className="text-xs text-slate-400">
        Pick one or more dates, then mark them as Available, Booked, or Unavailable. Hosts will see this on your public profile.
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2 inline-block">
        <Calendar
          mode="multiple"
          selected={selected}
          onSelect={setSelected}
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          fromDate={new Date()}
          numberOfMonths={1}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => setStatusForSelected('available')} data-testid="calendar-status-available" className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-400/30">
          <CheckCircle2 className="h-4 w-4 mr-2" /> Mark Available
        </Button>
        <Button type="button" onClick={() => setStatusForSelected('booked')} data-testid="calendar-status-booked" className="bg-rose-500/20 hover:bg-rose-500/30 text-rose-200 border border-rose-400/30">
          <XCircle className="h-4 w-4 mr-2" /> Mark Booked
        </Button>
        <Button type="button" onClick={() => setStatusForSelected('unavailable')} data-testid="calendar-status-unavailable" className="bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 border border-slate-400/30">
          <Ban className="h-4 w-4 mr-2" /> Mark Unavailable
        </Button>
        <Button type="button" onClick={removeSelected} data-testid="calendar-clear-selection" variant="outline" className="border-white/10 bg-transparent text-slate-300">
          <Trash2 className="h-4 w-4 mr-2" /> Clear status
        </Button>
        <Button type="button" onClick={save} disabled={saving || dirtyCount === 0} data-testid="calendar-save-button" className="btn-rainbow text-white border-0 ml-auto">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save{dirtyCount ? ` (${dirtyCount})` : ''}
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} /> {m.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * AvailabilityViewer — read-only calendar for the public artist profile.
 * Shows next 90 days with status colour coding.
 */
export const AvailabilityViewer = ({ artistId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!artistId) return;
    const today = new Date();
    const end = new Date(); end.setDate(today.getDate() + 90);
    api.get(`/artists/${artistId}/calendar`, {
      params: { from_date: fmtDate(today), to_date: fmtDate(end) },
    }).then(({ data }) => setEntries(data.entries)).finally(() => setLoading(false));
  }, [artistId]);

  const modifiers = useMemo(() => {
    const av = [], bo = [], un = [];
    entries.forEach((e) => {
      const d = parseISO(e.date);
      if (e.status === 'available') av.push(d);
      else if (e.status === 'booked') bo.push(d);
      else if (e.status === 'unavailable') un.push(d);
    });
    return { available: av, booked: bo, unavailable: un };
  }, [entries]);

  const modifiersClassNames = {
    available: 'bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/30',
    booked: 'bg-rose-500/20 text-rose-200 ring-1 ring-rose-400/30',
    unavailable: 'bg-slate-500/20 text-slate-300 ring-1 ring-slate-400/30 line-through',
  };

  if (loading) return <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-fuchsia-400" /></div>;
  if (entries.length === 0) {
    return <div className="text-sm text-slate-400 py-3">This artist hasn&apos;t shared their calendar yet — assume they&apos;re open to enquire.</div>;
  }

  return (
    <div data-testid="artist-calendar-viewer" className="space-y-3">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-2 inline-block">
        <Calendar
          mode="single"
          modifiers={modifiers}
          modifiersClassNames={modifiersClassNames}
          fromDate={new Date()}
          numberOfMonths={1}
          disabled
        />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-full ${m.dot}`} /> {m.label}
          </span>
        ))}
      </div>
    </div>
  );
};
